import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { setFloatingHost, useRecordingStore } from '@/state/recording-store';
import { FloatingControls } from './FloatingControls';

interface DocumentPictureInPicture {
  requestWindow(options: { width: number; height: number }): Promise<Window>;
}

const pictureInPicture = (): DocumentPictureInPicture | undefined =>
  typeof window === 'undefined' ? undefined : (window as Window & { documentPictureInPicture?: DocumentPictureInPicture }).documentPictureInPicture;

/** Chrome and Edge on desktop can keep a small window above every other app. */
export const floatingSupported = (): boolean => pictureInPicture() !== undefined;

/** The new window starts empty: it gets the page's styles, and fonts resolve against this site. */
function adoptStyles(target: Document): void {
  const base = target.createElement('base');
  base.href = location.origin;
  target.head.append(base);
  for (const sheet of [...document.styleSheets]) {
    const owner = sheet.ownerNode;
    if (owner instanceof HTMLLinkElement) {
      const link = target.createElement('link');
      link.rel = 'stylesheet';
      link.href = owner.href;
      target.head.append(link);
      continue;
    }
    try {
      const style = target.createElement('style');
      style.textContent = [...sheet.cssRules].map((rule) => rule.cssText).join('\n');
      target.head.append(style);
    } catch {
      // A sheet that can't be read (another origin) is skipped; ours always can be.
    }
  }
}

let current: { readonly window: Window; readonly root: Root } | null = null;

/**
 * Floating recording controls in a Document Picture-in-Picture window: they stay
 * above the window being shared, so the person never has to come back to this
 * tab to pause or finish. Always dark, since it sits over arbitrary content.
 */
export function installFloatingHost(): void {
  setFloatingHost({
    supported: floatingSupported(),
    async open() {
      const api = pictureInPicture();
      if (!api) return false;
      if (current && !current.window.closed) return true;
      try {
        // With speaker notes the window grows into a small teleprompter above the controls.
        const notes = useRecordingStore.getState().notes.trim().length > 0;
        const pip = await api.requestWindow(notes ? { width: 440, height: 300 } : { width: 380, height: 76 });
        adoptStyles(pip.document);
        pip.document.documentElement.dataset.theme = 'dark';
        pip.document.documentElement.lang = document.documentElement.lang;
        pip.document.title = document.title;
        const container = pip.document.createElement('div');
        container.className = 'h-full';
        pip.document.body.append(container);
        const root = createRoot(container);
        root.render(
          <StrictMode>
            <FloatingControls />
          </StrictMode>,
        );
        current = { window: pip, root };
        pip.addEventListener('pagehide', () => {
          root.unmount();
          if (current?.window === pip) current = null;
          useRecordingStore.setState({ floating: false });
        });
        return true;
      } catch {
        // No user gesture left, or the browser said no: the in-page controls remain.
        return false;
      }
    },
    close() {
      current?.window.close();
      current = null;
    },
  });
}
